import { useGrowthBook } from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { isExchangeInProgress, resolveFinalPriceVariant } from 'utils/trips/finalPriceExperiment';

/**
 * Whether this user sees the markup-inclusive ("final") price, for React components.
 *
 * Applies the rule in `utils/trips/finalPriceExperiment`, sharing its tab latch with the
 * non-React reader `isFinalPriceVariantActive`, so a card and the sort or tracker behind it always
 * agree. The feature is evaluated only after every other gate holds, so a surface whose price
 * would look the same in both arms never logs an exposure.
 *
 * Reads the instance imperatively rather than through `useGrowthBookFeatureValue`, which
 * evaluates on every render, flag off or not. Unlike INS-1288's hook, the answer does not
 * converge when GrowthBook re-renders the tree: the first gated read latches the tab, so a price
 * never changes on screen.
 *
 * @param {object} [gate] - What the surface is about to render.
 * @param {boolean} [gate.hasMarkup] - Whether the rendered pricing has a companion above its total
 *   (`hasMarkupAboveTotal`).
 * @param {boolean} [gate.hasDisplayTotal] - Whether it carries an INS-1288 display total.
 * @param {boolean} [gate.isExchange] - Whether the flow is an exchange. When omitted, it is read
 *   from the exchange slice (`isExchangeInProgress`).
 * @returns {boolean} Whether to paint the final price.
 */
const useFinalPriceExperiment = ({ hasMarkup, hasDisplayTotal, isExchange } = {}) => {
  const growthBookInstance = useGrowthBook();
  const { brand } = useWhitelabelEnvs() || {};
  const features = useWhitelabelFeatures();
  const exchangeInProgress = useSelector((state) => isExchangeInProgress(state.exchange));

  return resolveFinalPriceVariant({
    features,
    brand,
    isExchange: isExchange ?? exchangeInProgress,
    hasDisplayTotal,
    hasMarkup,
    growthBookInstance,
  });
};

export default useFinalPriceExperiment;
